import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { ShopService } from '@app/services/shops/shop.service';
import { AddressFormComponent } from '@app/sharedModule/components/address-form/address-form.component';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { CheckoutSettings } from '@pdqdev/pdq-shared/dist/entities';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { ShopPlugin } from '@pdqdev/pdq-shared/dist/interfaces';
import { v4 as uuidv4 } from 'uuid';
import { Subject, combineLatest, Observable, of } from 'rxjs';
import { filter, finalize, takeUntil, switchMap, tap, catchError, map } from 'rxjs/operators';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import { ConditionsRules } from './components/product-page-conditions/configs';
import { ProductPromiseService } from './product-promise.service';
import {
  ARRIVAL_DATE_VARIABLE_SELECTOR,
  CUTOFF_TIME_VARIABLE_SELECTOR,
  SHIPPING_RATE_VARIABLE_SELECTOR,
  SHIPPING_ZIPCODE_VARIABLE_SELECTOR,
  validateProServiceShippingPattern,
  getDefaultProductPageConfig,
} from './utils';
import {
  DEFAULT_CUTOFF_TIMER_OPTIONS,
  DEFAULT_DELIVERY_DATE_OPTIONS,
  DEFAULT_ICON_URL,
  DEFAULT_LABEL_OPTIONS,
  DEFAULT_LOCATION_ERROR_MESSAGE,
  DEFAULT_LOCATION_ERROR_MESSAGE_COLOR,
  DEFAULT_LOCATION_INPUT_OPTIONS,
  DEFAULT_SHIPPING_METHOD_LINE_OPTIONS,
  DEFAULT_TRUST_SIGNALS_OPTIONS,
  TRUST_SIGNALS_MAX_COUNT,
  TRUST_SIGNALS_MIN_COUNT,
  createDefaultTrustSignal,
} from './defaults';
import { ROUTE_URLS, SUPPORT_URLS } from '../constants';
import { validateEmptyText, validateLongDeliveryField } from './product-page.utils';

type ProductPageTab = 'settings' | 'configurations' | 'conditions';

const trustSignalsCountValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const length = (control as UntypedFormArray).controls?.length ?? 0;
  if (length < TRUST_SIGNALS_MIN_COUNT) return { trustSignalsMinCount: true };
  if (length > TRUST_SIGNALS_MAX_COUNT) return { trustSignalsMaxCount: true };
  return null;
};

@Component({
  selector: 'product-page',
  templateUrl: './product-page.component.html',
  styleUrls: ['./product-page.component.scss'],
  standalone: false,
})
export class ProductPageComponent implements OnInit, OnDestroy {
  formGroup: UntypedFormGroup;
  tagInstructionsUrl = SUPPORT_URLS.PRODUCT_PAGE.TAG_INSTRUCTIONS;
  blockInstructionsUrl = SUPPORT_URLS.PRODUCT_PAGE.BLOCK_INSTRUCTIONS;
  saving: boolean = false;
  tagCodeControl = new UntypedFormControl({ value: '', disabled: true });
  testParameterControl = new UntypedFormControl({ value: '', disabled: true });
  shop: Shop;
  destroy$ = new Subject();
  iconPreview: any = '';
  pluginType = ShopPlugin.ProductPagePromise.PluginType.DEFAULT;
  PluginType = ShopPlugin.ProductPagePromise.PluginType;
  layoutMode: ShopPlugin.ProductPagePromise.LayoutMode = ShopPlugin.ProductPagePromise.LayoutMode.BASIC;
  LayoutMode = ShopPlugin.ProductPagePromise.LayoutMode;
  loadedConfig: ShopPlugin.ProductPagePromise.Config | ShopPlugin.ProService.Config;
  themeEditorUrl;
  defaultIcon = DEFAULT_ICON_URL;
  showList = false;
  promiseId: number | null = null;
  previewConfig: ShopPlugin.ProductPagePromise.Config | null = null;
  settings$: Observable<CheckoutSettings | null> = this.store.select(fromCheckoutReducer.selectSettings);

  readonly tabs: { label: string; value: ProductPageTab }[] = [
    { label: 'Settings', value: 'settings' },
    { label: 'Configurations', value: 'configurations' },
    { label: 'Display Conditions', value: 'conditions' },
  ];
  activeTab: ProductPageTab = 'settings';

  constructor(
    private productPromiseService: ProductPromiseService,
    private toastService: ToastService,
    public shopService: ShopService,
    private rulesDynamicFormService: RulesDynamicFormService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<fromCheckoutReducer.State>,
  ) {}

  get testModeExtample(): string {
    const id = this.formGroup?.get('id')?.value;

    if (id) {
      return `https://storeName.myshopify.com/productName?testProductPromise=${id}`;
    } else {
      return 'https://storeName.myshopify.com/productName?testProductPromiseId';
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.store.dispatch(CheckoutActions.loadCheckoutSettings());

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.checkRoute();
      });

    this.checkRoute();
  }

  onTabChange(value: ProductPageTab): void {
    this.activeTab = value;
  }

  private checkRoute() {
    const url = this.router.url;
    if (url.includes('/product-page/new')) {
      this.promiseId = null;
      this.showList = false;
      this.initSubscriptionsForNew();
    } else {
      const id = this.route.snapshot.params['id'];
      if (id && !isNaN(+id)) {
        this.promiseId = +id;
        this.showList = false;
        this.initSubscriptions();
      } else {
        this.promiseId = null;
        this.showList = true;
        this.formGroup = null;
        this.loadedConfig = null;
        this.loadPreviewConfig();
      }
    }
  }

  onNewProductPage() {
    this.router.navigate(['/engage/product-page/new']);
  }

  private loadPreviewConfig() {
    this.shopService.shop$
      .pipe(
        filter((shop) => !!shop),
        tap((shop) => {
          this.shop = shop;
          this.themeEditorUrl = `https://${shop.name}/admin/themes/current/editor?template=product`;
        }),
        switchMap((shop) =>
          this.productPromiseService.list().pipe(
            map((promises) => ({ shop, promises })),
            catchError((err) => {
              console.error(err);
              this.previewConfig = null;
              return of(null);
            }),
          ),
        ),
        filter((result) => result !== null),
        switchMap(({ shop, promises }) => {
          const sortedPromises = (promises || []).sort((a, b) => a.id - b.id);
          const activePromise = sortedPromises.find((p) => p.isActive);
          if (activePromise) {
            return this.productPromiseService.getById(activePromise.id).pipe(
              map((config) => ({ shop, config })),
              catchError((err) => {
                console.error(err);
                this.previewConfig = null;
                return of(null);
              }),
            );
          }
          this.previewConfig = null;
          return of(null);
        }),
        filter((result) => result !== null),
        takeUntil(this.destroy$),
      )
      .subscribe(({ shop, config }) => {
        this.previewConfig = config;
        this.pluginType =
          shop.metadata.shopPluginType ?? config?.pluginType ?? ShopPlugin.ProductPagePromise.PluginType.DEFAULT;
      });
  }

  validateShippingPattern(type: 'fastest' | 'free'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value:
        | ShopPlugin.ProductPagePromise.FastestShippingOptions
        | ShopPlugin.ProductPagePromise.CheapestShippingOptions = control.value;
      const variables = [ARRIVAL_DATE_VARIABLE_SELECTOR];
      if ((value as ShopPlugin.ProductPagePromise.FastestShippingOptions)?.showPrice) {
        variables.push(SHIPPING_RATE_VARIABLE_SELECTOR);
      }
      if (value.showCutOffTime) {
        variables.push(CUTOFF_TIME_VARIABLE_SELECTOR);
      }
      if (
        type === 'fastest' &&
        !this.shop?.metadata?.pdpAddressSelectorNotRequired &&
        this.formGroup?.get('design.template')?.value === ShopPlugin.ProductPagePromise.DesignTemplate.MINIMAL
      ) {
        variables.push(SHIPPING_ZIPCODE_VARIABLE_SELECTOR);
      }
      const errors: { missingVariables?: string; missingCheapestPatternVariables?: string } = {};
      const patternKey = type === 'fastest' ? 'pattern' : 'freeShippingPattern';
      const patternValidationErrors = variables
        .map((variable) => (value[patternKey].includes(variable) ? null : variable))
        .filter(Boolean);
      if (patternValidationErrors.length) {
        errors.missingVariables = `${patternValidationErrors.join(', ')} must be included`;
      }

      const cheapestShippingValue = value as ShopPlugin.ProductPagePromise.CheapestShippingOptions;
      if (type === 'free' && !cheapestShippingValue.showOnlyFreeShipping) {
        const cheapestPatternValidationErrors = variables
          .map((variable) => (cheapestShippingValue.cheapestShippingPattern?.includes(variable) ? null : variable))
          .filter(Boolean);
        if (cheapestPatternValidationErrors.length) {
          errors.missingCheapestPatternVariables = `${cheapestPatternValidationErrors.join(', ')} must be included`;
        }
      }
      return Object.keys(errors).length ? errors : null;
    };
  }

  private initSubscriptionsForNew() {
    this.shopService.shop$
      .pipe(
        filter((shop) => !!shop),
        takeUntil(this.destroy$),
      )
      .subscribe((shop) => {
        this.shop = shop;
        this.themeEditorUrl = `https://${shop.name}/admin/themes/current/editor?template=product`;
        this.pluginType = shop.metadata.shopPluginType ?? ShopPlugin.ProductPagePromise.PluginType.DEFAULT;
        this.loadedConfig = null;
        this.initForm(undefined);
      });
  }

  private initSubscriptions() {
    if (this.promiseId) {
      this.shopService.shop$
        .pipe(
          filter((shop) => !!shop),
          tap((shop) => {
            this.shop = shop;
            this.themeEditorUrl = `https://${shop.name}/admin/themes/current/editor?template=product`;
          }),
          switchMap((shop) =>
            this.productPromiseService.getById(this.promiseId!).pipe(
              map((config) => ({ shop, config })),
              catchError((err) => {
                console.error(err);
                this.toastService.showToast({ mode: 'danger', message: 'Failed to load product promise' });
                this.router.navigate(['/engage/product-page']);
                return of(null);
              }),
            ),
          ),
          filter((result) => result !== null),
          takeUntil(this.destroy$),
        )
        .subscribe(({ shop, config }) => {
          this.loadedConfig = config;
          this.pluginType =
            shop.metadata.shopPluginType ?? config?.pluginType ?? ShopPlugin.ProductPagePromise.PluginType.DEFAULT;
          this.initForm(config);
        });
    } else {
      combineLatest([this.shopService.shop$, this.productPromiseService.productPageSettings$])
        .pipe(
          filter(([shop, _]) => !!shop),
          takeUntil(this.destroy$),
        )
        .subscribe(([shop, config]) => {
          this.loadedConfig = config;
          this.pluginType =
            shop.metadata.shopPluginType ?? config?.pluginType ?? ShopPlugin.ProductPagePromise.PluginType.DEFAULT;
          this.shop = shop;
          this.themeEditorUrl = `https://${shop.name}/admin/themes/current/editor?template=product`;
          this.initForm(config);
        });
    }
  }

  private initProServiceForm(config?: ShopPlugin.ProService.Config) {
    if (this.pluginType !== ShopPlugin.ProductPagePromise.PluginType.PRO_SERVICE) {
      return {};
    }

    const initialValue = config?.inventoryConfig ?? ShopPlugin.ProService.DefaultConfigValue;

    return {
      inventoryConfig: new UntypedFormGroup({
        enabled: new UntypedFormControl(initialValue?.enabled),
        inStockPattern: new UntypedFormControl(
          initialValue.inStockPattern,
          validateProServiceShippingPattern(['ship_date']),
        ),
        outOfStockPattern: new UntypedFormControl(
          initialValue?.outOfStockPattern,
          validateProServiceShippingPattern(['key_metafield']),
        ),
        partiallyInStockPattern: new UntypedFormControl(
          initialValue?.partiallyInStockPattern,
          validateProServiceShippingPattern(['inventory_snapshot', 'ship_date', 'key_metafield']),
        ),
        keyMetafield: new UntypedFormControl(initialValue?.keyMetafield, validateEmptyText()),
        fallbackText: new UntypedFormControl(initialValue?.fallbackText, validateProServiceShippingPattern([])),
        quantityInputSelector: new UntypedFormControl(initialValue.quantityInputSelector, validateEmptyText()),
      }),
    };
  }

  private initProductPromiseForm(config?: ShopPlugin.ProductPagePromise.Config) {
    if (this.pluginType !== ShopPlugin.ProductPagePromise.PluginType.DEFAULT) {
      return {};
    }
    return {
      fastestShipping: new UntypedFormGroup(
        {
          enabled: new UntypedFormControl(config?.fastestShipping?.enabled ?? true),
          hideIfLongDelivery: new UntypedFormControl(config?.fastestShipping?.hideIfLongDelivery ?? false),
          hideIfLongDeliveryDaysThreshold: new UntypedFormControl(
            config?.fastestShipping?.hideIfLongDeliveryDaysThreshold ?? 0,
          ),
          pattern: new UntypedFormControl(
            config?.fastestShipping?.pattern ??
              `Arrives on ${ARRIVAL_DATE_VARIABLE_SELECTOR}${
                config?.fastestShipping?.showPrice ? ` for ${SHIPPING_RATE_VARIABLE_SELECTOR}` : ''
              }${
                config?.design?.template === ShopPlugin.ProductPagePromise.DesignTemplate.DEFAULT
                  ? ''
                  : ` to ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`
              }${config?.fastestShipping?.showCutOffTime ? ` order within ${CUTOFF_TIME_VARIABLE_SELECTOR}` : ''}`,
          ),

          fallbackText: new UntypedFormControl(
            config?.fastestShipping?.fallbackText ?? 'No expedited shipping options to your location',
          ),
          showPrice: new UntypedFormControl(config?.fastestShipping?.showPrice ?? false),
          showCutOffTime: new UntypedFormControl(config?.fastestShipping?.showCutOffTime ?? false),
        },
        [this.validateShippingPattern('fastest'), validateLongDeliveryField()],
      ),
      cheapestShipping: new UntypedFormGroup(
        {
          enabled: new UntypedFormControl(config?.cheapestShipping?.enabled ?? true),
          hideIfLongDelivery: new UntypedFormControl(config?.cheapestShipping?.hideIfLongDelivery ?? false),
          hideIfLongDeliveryDaysThreshold: new UntypedFormControl(
            config?.cheapestShipping?.hideIfLongDeliveryDaysThreshold ?? 0,
          ),
          cartValueThreshold: new UntypedFormControl(
            config?.cheapestShipping?.cartValueThreshold ?? 0,
            Validators.required,
          ),
          freeShippingPattern: new UntypedFormControl(
            config?.cheapestShipping?.freeShippingPattern ??
              `Free shipping ${ARRIVAL_DATE_VARIABLE_SELECTOR}${
                config?.cheapestShipping?.showCutOffTime ? ` order within ${CUTOFF_TIME_VARIABLE_SELECTOR}` : ''
              }`,
          ),
          fallbackText: new UntypedFormControl(
            config?.cheapestShipping?.fallbackText ?? 'Free shipping will be calculated on checkout',
          ),
          showOnlyFreeShipping: new UntypedFormControl(config?.cheapestShipping?.showOnlyFreeShipping ?? true),
          cheapestShippingPattern: new UntypedFormControl(
            config?.cheapestShipping?.cheapestShippingPattern ??
              `Cheapest shipping arrives ${ARRIVAL_DATE_VARIABLE_SELECTOR}`,
            validateEmptyText(),
          ),
          showCutOffTime: new UntypedFormControl(config?.cheapestShipping?.showCutOffTime ?? false),
        },
        [this.validateShippingPattern('free'), validateLongDeliveryField()],
      ),
    };
  }

  initForm(config?: ShopPlugin.Config) {
    const configWithExtras = config as ShopPlugin.Config & {
      internalName?: string;
      internalDescription?: string;
      usageToken?: string;
    };
    this.formGroup = new UntypedFormGroup({
      testMode: new UntypedFormControl(config?.testMode ?? false),
      internalName: new UntypedFormControl(configWithExtras?.internalName ?? 'Product Promise', Validators.required),
      internalDescription: new UntypedFormControl(configWithExtras?.internalDescription ?? 'Product Promise'),
      usageToken: new UntypedFormControl(configWithExtras?.usageToken || uuidv4()),
      ...this.initProductPromiseForm(<ShopPlugin.ProductPagePromise.Config>config),
      ...this.initProServiceForm(<ShopPlugin.ProService.Config>config),
      addressEntry: new UntypedFormGroup({
        useGeolocation: new UntypedFormControl(!!config?.addressEntry?.useGeolocation),
        zipOnly: new UntypedFormControl(!!config?.addressEntry?.zipOnly),
        fallbackAddress: new UntypedFormControl(
          AddressFormComponent.createFormFromAddress(config?.addressEntry?.fallbackAddress).value,
        ),
        errorMessage: new UntypedFormControl(config?.addressEntry?.errorMessage ?? DEFAULT_LOCATION_ERROR_MESSAGE),
        errorMessageColor: new UntypedFormControl(
          config?.addressEntry?.errorMessageColor ?? DEFAULT_LOCATION_ERROR_MESSAGE_COLOR,
        ),
      }),
      pluginType: new UntypedFormControl(this.pluginType),
      layoutMode: new UntypedFormControl(
        (config as ShopPlugin.ProductPagePromise.Config)?.layoutMode ?? ShopPlugin.ProductPagePromise.LayoutMode.BASIC,
      ),
      advancedLayoutSettings: this.buildAdvancedLayoutSettings(
        (config as ShopPlugin.ProductPagePromise.Config)?.advancedLayoutSettings,
      ),
      design: new UntypedFormGroup({
        zipcodeInFooterDesign: new UntypedFormControl(
          config?.design?.template === ShopPlugin.ProductPagePromise.DesignTemplate.DEFAULT,
        ),
        template: new UntypedFormControl(
          config?.design?.template ?? ShopPlugin.ProductPagePromise.DesignTemplate.MINIMAL,
        ),
        widgetTitle: new UntypedFormControl(config?.design?.widgetTitle ?? 'Check shipping options'),
        inputPlaceholder: new UntypedFormControl(config?.design?.inputPlaceholder ?? 'Enter your address'),
        buttonText: new UntypedFormControl(config?.design?.buttonText ?? 'Check shipping'),
        backgroundColor: new UntypedFormControl(config?.design?.backgroundColor ?? '#ffffff'),
        fontColor: new UntypedFormControl(config?.design?.fontColor ?? '#000000'),
        cornerRadius: new UntypedFormControl(config?.design?.cornerRadius ?? 0),
        padding: new UntypedFormControl(config?.design?.padding ?? 0),
        buttonPadding: new UntypedFormControl(
          (config?.design as ShopPlugin.ProductPagePromise.DesignOptions & { buttonPadding?: number })?.buttonPadding ??
            0,
        ),
        iconUrl: new UntypedFormControl(
          config?.design && 'iconUrl' in config.design ? config.design.iconUrl || '' : this.defaultIcon,
        ),
      }),
      conditions: new UntypedFormGroup({
        conditionsRules: this.rulesDynamicFormService.initRulesFormArray(
          config?.conditions?.conditionsRules,
          ConditionsRules,
        ),
      }),
    });

    if (config?.id) {
      this.formGroup.addControl('id', new UntypedFormControl(config.id));
    }

    this.updateTagCode();
    this.updateTestParameter();
    const usageTokenControl = this.formGroup.get('usageToken');
    if (usageTokenControl) {
      usageTokenControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.updateTagCode();
      });
    }
    const idControl = this.formGroup.get('id');
    if (idControl) {
      idControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.updateTestParameter();
      });
    }

    const layoutModeControl = this.formGroup.get('layoutMode');
    if (layoutModeControl) {
      this.layoutMode = layoutModeControl.value;
      this.syncAdvancedLayoutEnabled(this.layoutMode);
      layoutModeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        this.layoutMode = value;
        this.syncAdvancedLayoutEnabled(value);
      });
    }

    this.formGroup.markAsPristine();
  }

  private syncAdvancedLayoutEnabled(mode: ShopPlugin.ProductPagePromise.LayoutMode): void {
    const advancedLayoutSettings = this.formGroup.get('advancedLayoutSettings');
    if (!advancedLayoutSettings) {
      return;
    }
    if (mode === ShopPlugin.ProductPagePromise.LayoutMode.ADVANCED) {
      advancedLayoutSettings.enable({ emitEvent: false });
    } else {
      advancedLayoutSettings.disable({ emitEvent: false });
    }
  }

  updateTagCode() {
    if (!this.formGroup) {
      return;
    }
    const usageToken = this.formGroup.get('usageToken')?.value;
    if (usageToken) {
      this.tagCodeControl.setValue(`<PDQProductPromise token='${usageToken}'></PDQProductPromise>`, {
        emitEvent: false,
      });
    } else {
      this.tagCodeControl.setValue('<PDQProductPromise></PDQProductPromise>', { emitEvent: false });
    }
  }

  updateTestParameter() {
    if (!this.formGroup) {
      return;
    }
    const id = this.formGroup.get('id')?.value;
    if (id) {
      this.testParameterControl.setValue(`?testProductPromise=${id}`, {
        emitEvent: false,
      });
    } else {
      this.testParameterControl.setValue('?testProductPromiseId', { emitEvent: false });
    }
  }

  prepareSavePayload() {
    const value = this.formGroup.getRawValue();

    switch (this.pluginType) {
      case ShopPlugin.ProductPagePromise.PluginType.PRO_SERVICE:
        delete value.fastestShipping;
        delete value.freeShipping;
        break;
      case ShopPlugin.ProductPagePromise.PluginType.DEFAULT:
        delete value.inStockConfig;
        delete value.outOfStockConfig;
        break;
    }

    value.template = value.design.zipcodeInFooterDesign
      ? ShopPlugin.ProductPagePromise.DesignTemplate.DEFAULT
      : ShopPlugin.ProductPagePromise.DesignTemplate.MINIMAL;
    return value;
  }
  onSave() {
    this.saving = true;
    const payload = this.prepareSavePayload();
    const configId = payload.id || this.promiseId;

    if (configId) {
      this.productPromiseService
        .update(configId, payload, this.shop)
        .pipe(finalize(() => (this.saving = false)))
        .subscribe(
          () => {
            this.toastService.showToast({ mode: 'success', message: 'Settings saved successfully' });
            this.productPromiseService.initProductPageSettings();
            this.router.navigate(['/engage/product-page']);
          },
          (err) => {
            console.error(err);
            this.toastService.showToast({ mode: 'danger', message: 'Failed to save settings' });
          },
        );
    } else {
      this.productPromiseService
        .setProductPageSettings(payload, this.shop)
        .pipe(finalize(() => (this.saving = false)))
        .subscribe(
          () => {
            this.toastService.showToast({ mode: 'success', message: 'Settings saved successfully' });
            this.router.navigate(['/engage/product-page']);
          },
          (err) => {
            console.error(err);
            this.toastService.showToast({ mode: 'danger', message: 'Failed to save settings' });
          },
        );
    }
  }

  navigateToCheckoutSettings() {
    this.router.navigate([ROUTE_URLS.CHECKOUT_SETTINGS]);
  }

  onCancel() {
    this.router.navigate(['/engage/product-page']);
  }

  private buildAdvancedLayoutSettings(loaded?: ShopPlugin.ProductPagePromise.AdvancedLayoutSettings): UntypedFormGroup {
    const defaults = this.getDefaultLayoutComponents();
    const loadedComponents = loaded?.components?.length ? loaded.components : [];
    const loadedTypes = new Set(loadedComponents.map((c) => c.type));
    const missingDefaults = defaults.filter((d) => !loadedTypes.has(d.type));
    const components = loadedComponents.length ? [...loadedComponents, ...missingDefaults] : defaults;
    return new UntypedFormGroup({
      components: new UntypedFormArray(components.map((component) => this.buildComponentFormGroup(component))),
    });
  }

  private getDefaultLayoutComponents(): ShopPlugin.ProductPagePromise.LayoutComponent[] {
    return [
      {
        id: uuidv4(),
        type: ShopPlugin.ProductPagePromise.LayoutComponentType.LABEL,
        enabled: true,
        options: { ...DEFAULT_LABEL_OPTIONS },
      },
      {
        id: uuidv4(),
        type: ShopPlugin.ProductPagePromise.LayoutComponentType.DELIVERY_DATE,
        enabled: true,
        options: { ...DEFAULT_DELIVERY_DATE_OPTIONS },
      },
      {
        id: uuidv4(),
        type: ShopPlugin.ProductPagePromise.LayoutComponentType.SHIPPING_METHOD_LINE,
        enabled: true,
        options: { ...DEFAULT_SHIPPING_METHOD_LINE_OPTIONS },
      },
      {
        id: uuidv4(),
        type: ShopPlugin.ProductPagePromise.LayoutComponentType.LOCATION_INPUT,
        enabled: true,
        options: { ...DEFAULT_LOCATION_INPUT_OPTIONS },
      },
      {
        id: uuidv4(),
        type: ShopPlugin.ProductPagePromise.LayoutComponentType.CUTOFF_TIMER,
        enabled: false,
        options: { ...DEFAULT_CUTOFF_TIMER_OPTIONS },
      },
      {
        id: uuidv4(),
        type: ShopPlugin.ProductPagePromise.LayoutComponentType.TRUST_SIGNALS,
        enabled: false,
        options: { ...DEFAULT_TRUST_SIGNALS_OPTIONS, signals: [createDefaultTrustSignal()] },
      },
    ];
  }

  private buildComponentFormGroup(component: ShopPlugin.ProductPagePromise.LayoutComponent): UntypedFormGroup {
    const isDeliveryDate = component.type === ShopPlugin.ProductPagePromise.LayoutComponentType.DELIVERY_DATE;
    return new UntypedFormGroup({
      id: new UntypedFormControl(component.id),
      type: new UntypedFormControl(component.type),
      enabled: new UntypedFormControl(isDeliveryDate ? true : component.enabled),
      options: this.buildOptionsFormGroup(component),
    });
  }

  private buildOptionsFormGroup(component: ShopPlugin.ProductPagePromise.LayoutComponent): UntypedFormGroup {
    switch (component.type) {
      case ShopPlugin.ProductPagePromise.LayoutComponentType.LABEL: {
        const options = (component.options ?? {}) as ShopPlugin.ProductPagePromise.LabelOptions;
        return new UntypedFormGroup({
          text: new UntypedFormControl(options.text ?? DEFAULT_LABEL_OPTIONS.text),
          iconUrl: new UntypedFormControl(options.iconUrl ?? DEFAULT_LABEL_OPTIONS.iconUrl),
          customIconUrl: new UntypedFormControl(options.customIconUrl ?? null),
        });
      }
      case ShopPlugin.ProductPagePromise.LayoutComponentType.DELIVERY_DATE: {
        const options = (component.options ?? {}) as ShopPlugin.ProductPagePromise.DeliveryDateOptions;
        return new UntypedFormGroup({
          calculationMethod: new UntypedFormControl(
            options.calculationMethod ?? DEFAULT_DELIVERY_DATE_OPTIONS.calculationMethod,
          ),
          displayMessage: new UntypedFormControl(
            options.displayMessage ?? DEFAULT_DELIVERY_DATE_OPTIONS.displayMessage,
          ),
          promiseFormat: new UntypedFormControl(options.promiseFormat ?? DEFAULT_DELIVERY_DATE_OPTIONS.promiseFormat),
          fallbackMessage: new UntypedFormControl(
            options.fallbackMessage ?? DEFAULT_DELIVERY_DATE_OPTIONS.fallbackMessage,
          ),
          hideIfLongDelivery: new UntypedFormControl(
            options.hideIfLongDelivery ?? DEFAULT_DELIVERY_DATE_OPTIONS.hideIfLongDelivery,
          ),
          hideIfLongDeliveryDaysThreshold: new UntypedFormControl(
            options.hideIfLongDeliveryDaysThreshold ?? DEFAULT_DELIVERY_DATE_OPTIONS.hideIfLongDeliveryDaysThreshold,
          ),
        });
      }
      case ShopPlugin.ProductPagePromise.LayoutComponentType.SHIPPING_METHOD_LINE: {
        const options = (component.options ?? {}) as ShopPlugin.ProductPagePromise.ShippingMethodLineOptions;
        const allowAllMethods = options.allowAllMethods ?? DEFAULT_SHIPPING_METHOD_LINE_OPTIONS.allowAllMethods;
        return new UntypedFormGroup({
          priceDisplay: new UntypedFormControl(
            options.priceDisplay ?? DEFAULT_SHIPPING_METHOD_LINE_OPTIONS.priceDisplay,
          ),
          allowAllMethods: new UntypedFormControl(allowAllMethods),
          allowedMethodNames: new UntypedFormControl(
            options.allowedMethodNames ?? DEFAULT_SHIPPING_METHOD_LINE_OPTIONS.allowedMethodNames,
            allowAllMethods ? [] : [Validators.required, Validators.minLength(1)],
          ),
          allowShopperToChangeMethod: new UntypedFormControl(
            options.allowShopperToChangeMethod ?? DEFAULT_SHIPPING_METHOD_LINE_OPTIONS.allowShopperToChangeMethod,
          ),
          buttonLabel: new UntypedFormControl(options.buttonLabel ?? DEFAULT_SHIPPING_METHOD_LINE_OPTIONS.buttonLabel),
        });
      }
      case ShopPlugin.ProductPagePromise.LayoutComponentType.LOCATION_INPUT: {
        const options = (component.options ?? {}) as ShopPlugin.ProductPagePromise.LocationInputOptions;
        return new UntypedFormGroup({
          type: new UntypedFormControl(options.type ?? DEFAULT_LOCATION_INPUT_OPTIONS.type),
          emptyStateDescription: new UntypedFormControl(
            options.emptyStateDescription ?? DEFAULT_LOCATION_INPUT_OPTIONS.emptyStateDescription,
          ),
          fieldPlaceholder: new UntypedFormControl(
            options.fieldPlaceholder ?? DEFAULT_LOCATION_INPUT_OPTIONS.fieldPlaceholder,
          ),
          buttonLabel: new UntypedFormControl(options.buttonLabel ?? DEFAULT_LOCATION_INPUT_OPTIONS.buttonLabel),
          buttonColor: new UntypedFormControl(options.buttonColor ?? DEFAULT_LOCATION_INPUT_OPTIONS.buttonColor),
          changeAddressLinkText: new UntypedFormControl(
            options.changeAddressLinkText ?? DEFAULT_LOCATION_INPUT_OPTIONS.changeAddressLinkText,
          ),
          deliveringToText: new UntypedFormControl(
            options.deliveringToText ?? DEFAULT_LOCATION_INPUT_OPTIONS.deliveringToText,
          ),
          autoDetect: new UntypedFormControl(options.autoDetect ?? DEFAULT_LOCATION_INPUT_OPTIONS.autoDetect),
          fallbackAddress: new UntypedFormControl(
            AddressFormComponent.createFormFromAddress(options.fallbackAddress).value,
          ),
          errorMessage: new UntypedFormControl(options.errorMessage ?? DEFAULT_LOCATION_INPUT_OPTIONS.errorMessage),
          errorMessageColor: new UntypedFormControl(
            options.errorMessageColor ?? DEFAULT_LOCATION_INPUT_OPTIONS.errorMessageColor,
          ),
        });
      }
      case ShopPlugin.ProductPagePromise.LayoutComponentType.LINE_SEPARATOR:
        return new UntypedFormGroup({});
      case ShopPlugin.ProductPagePromise.LayoutComponentType.CUTOFF_TIMER: {
        const options = (component.options ?? {}) as ShopPlugin.ProductPagePromise.CutoffTimerOptions;
        return new UntypedFormGroup({
          position: new UntypedFormControl(options.position ?? DEFAULT_CUTOFF_TIMER_OPTIONS.position),
          displayMessage: new UntypedFormControl(options.displayMessage ?? DEFAULT_CUTOFF_TIMER_OPTIONS.displayMessage),
          iconUrl: new UntypedFormControl(options.iconUrl ?? DEFAULT_CUTOFF_TIMER_OPTIONS.iconUrl),
          customIconUrl: new UntypedFormControl(options.customIconUrl ?? DEFAULT_CUTOFF_TIMER_OPTIONS.customIconUrl),
          backgroundColor: new UntypedFormControl(
            options.backgroundColor ?? DEFAULT_CUTOFF_TIMER_OPTIONS.backgroundColor,
          ),
        });
      }
      case ShopPlugin.ProductPagePromise.LayoutComponentType.TRUST_SIGNALS: {
        const options = (component.options ?? {}) as ShopPlugin.ProductPagePromise.TrustSignalsOptions;
        const signals = options.signals ?? [];
        return new UntypedFormGroup({
          signals: new UntypedFormArray(
            signals.map((signal) => this.buildTrustSignalFormGroup(signal)),
            trustSignalsCountValidator,
          ),
        });
      }
      default:
        return new UntypedFormGroup({});
    }
  }

  private buildTrustSignalFormGroup(signal: ShopPlugin.ProductPagePromise.TrustSignal): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl(signal.id ?? uuidv4()),
      text: new UntypedFormControl(signal.text ?? '', [Validators.required]),
      iconUrl: new UntypedFormControl(signal.iconUrl ?? ''),
      customIconUrl: new UntypedFormControl(signal.customIconUrl ?? ''),
    });
  }

  get formConfig(): ShopPlugin.ProductPagePromise.Config | null {
    if (!this.formGroup) {
      return null;
    }
    const value = this.formGroup.getRawValue();
    if (!value.pluginType) {
      value.pluginType = this.pluginType;
    }
    return value as ShopPlugin.ProductPagePromise.Config;
  }

  get previewConfigWithDefaults(): ShopPlugin.ProductPagePromise.Config {
    if (this.previewConfig) {
      return this.previewConfig;
    }
    return getDefaultProductPageConfig(this.pluginType);
  }
}
